import axios from 'axios';
import api from '@/lib/axios';

// Store-wide coaching conversation log (server/src/routes/coachingNotes.js).
// Level-gated per subject: the server 403s when the viewer's level isn't
// strictly above the subject's. That 403 is an expected, silent state for the
// expanded-row panels (they just hide the coaching block), so list() maps it
// to { canCoach: false } instead of throwing — and both calls suppress the
// global axios error toast for the same reason.

export interface CoachingNote {
  id: string;
  text: string;
  author: { id: string | null; name: string };
  createdAt: string;
  /** Stamped by the server on every text edit; null while never edited. */
  editedAt: string | null;
  /** Server-computed: viewer is the note's author, or an admin. Gates the
   *  Edit/Delete affordances so clients never duplicate permission logic. */
  canManage: boolean;
}

export interface CoachingNotesResult {
  notes: CoachingNote[];
  /** false only via the 403 level-gate path — hide the coaching UI entirely. */
  canCoach: boolean;
  /** 'own' when the viewer is a trainer logging about someone at or above
   *  them (store toggle): the list holds only their own notes. Absent on a
   *  403 and on responses from before this field existed. */
  scope?: 'all' | 'own';
}

export interface CoachablePerson {
  id: string;
  name: string;
  position: string;
}

export const coachingNotesService = {
  /** Everyone the viewer is allowed to coach (server-filtered: strictly below
   *  their level, active, same store, minus self) — the dashboard picker. */
  async coachable(): Promise<CoachablePerson[]> {
    const { data } = await api.get('/api/coaching-notes/coachable');
    return data?.people || [];
  },

  async list(userId: string): Promise<CoachingNotesResult> {
    try {
      const { data } = await api.get(`/api/coaching-notes/${userId}`, { suppressErrorToast: true });
      return { notes: data?.notes || [], canCoach: true, scope: data?.scope };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        return { notes: [], canCoach: false };
      }
      throw error;
    }
  },

  async create(userId: string, text: string): Promise<CoachingNote> {
    const { data } = await api.post(`/api/coaching-notes/${userId}`, { text }, { suppressErrorToast: true });
    return data;
  },

  /** Edit a note's text (author or admin only — server-enforced). */
  async update(userId: string, noteId: string, text: string): Promise<CoachingNote> {
    const { data } = await api.patch(`/api/coaching-notes/${userId}/${noteId}`, { text }, { suppressErrorToast: true });
    return data;
  },

  /** Hard-delete a note (author or admin only — server-enforced). */
  async remove(userId: string, noteId: string): Promise<void> {
    await api.delete(`/api/coaching-notes/${userId}/${noteId}`, { suppressErrorToast: true });
  },
};
